import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv/dist/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';
import styled from 'styled-components';

import { useDeepCompareEffect } from '@clatter/platform';
import { ProjectStepAction } from '../../../../interfaces';
import { DropdownSelect, FormButtons, NgCard, NgCardContent } from '../../../UI';
import { useGetEtlFunctionsAsSelectOptionsQuery, useUpdateProjectTemplateMutation } from '../../../../services';
import { useDispatch } from 'react-redux';
import { addNotification } from 'apps/ng/src/app/store';

interface SelectOption {
  label: string;
  value: string;
}

interface FormValues {
  enableEtl: boolean;
  customEtlArn: SelectOption | null;
}

interface EtlSectionProps {
  workspaceData: any;
  isLoading?: boolean;
  onAction: (action: ProjectStepAction) => void;
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const StyledCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};

  input {
    cursor: pointer;
  }

  label {
    pointer-events: none;
    user-select: none;
  }
`;

const StyledRowContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
`;

// ✅ Allow object or null for customEtlArn
const validation = {
  type: 'object',
  properties: {
    enableEtl: { type: 'boolean' },
    customEtlArn: {
      type: ['object', 'null'],
      properties: {
        label: { type: 'string' },
        value: { type: 'string' },
      },
      required: ['label', 'value'],
    },
  },
  required: [],
};

const defaultValues: FormValues = {
  enableEtl: false,
  customEtlArn: null,
};

const genericEtlOption = { label: 'Generic Etl', value: 'generic' };

export const EtlPage: FC<EtlSectionProps> = ({ workspaceData, isLoading: isParentLoading }) => {
  const { projectId: workspaceId } = useParams();
  const dispatch = useDispatch();

  const { data: etlFunctionsOptions, isLoading: isEtlFunctionsLoading } = useGetEtlFunctionsAsSelectOptionsQuery(
    undefined,
    {
      selectFromResult: ({ data, ...rest }) => {
        const augmentedData = data ? [genericEtlOption, ...data] : [genericEtlOption];
        return { data: augmentedData, ...rest };
      },
    },
  );

  const [updateProjectTemplate, { isLoading: isUpdateProjectTemplateLoading }] = useUpdateProjectTemplateMutation();

  const {
    register,
    control,
    reset,
    watch,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues,
    resolver: ajvResolver(validation as any, { formats: fullFormats }),
  });

  const isLoading = isParentLoading || isEtlFunctionsLoading || isUpdateProjectTemplateLoading;

  const { enableEtl } = watch();

  const onSubmit = async (data: FormValues) => {
    if (!workspaceId) return;

    try {
      const isGenericSelected = data?.customEtlArn?.value === 'generic';

      const payloadData = {
        has_etl: data.enableEtl,
        custom_etl_arn: data.enableEtl ? (isGenericSelected ? null : data.customEtlArn?.value ?? null) : null,
      };

      await updateProjectTemplate({
        projectTemplateId: +workspaceId,
        data: payloadData,
      }).unwrap();

      dispatch(
        addNotification({
          type: 'success',
          message: 'Etl has been successfully updated.',
        }),
      );
    } catch (error) {
      console.error('Error updating Etl', error);
      dispatch(
        addNotification({
          type: 'error',
          message: 'Error updating Etl',
        }),
      );
    }

    reset(data);
  };

  useDeepCompareEffect(() => {
    if (!workspaceData) return;

    const genericEtlOption = (etlFunctionsOptions || []).find((item: any) => item?.value === 'generic');

    const initialValues: FormValues = {
      ...defaultValues,
      enableEtl: !!workspaceData?.HasEtl,
      customEtlArn: workspaceData?.HasEtl
        ? workspaceData?.CustomEtlArn
          ? (etlFunctionsOptions || []).find((item: any) => item?.value === workspaceData?.CustomEtlArn) ||
            genericEtlOption ||
            null
          : genericEtlOption || null
        : genericEtlOption || null,
    };

    reset(initialValues);
  }, [workspaceData, etlFunctionsOptions]);

  return (
    <NgCard>
      <NgCardContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <StyledContainer>
            <StyledRowContainer>
              <StyledCheckbox>
                <input type="checkbox" {...register('enableEtl')} id="enableEtl" disabled={isLoading} />
                <label htmlFor="enableEtl">Enable Etl</label>
              </StyledCheckbox>
            </StyledRowContainer>

            {enableEtl && (
              <StyledRowContainer>
                <Controller
                  name="customEtlArn"
                  control={control}
                  render={({ field }) => {
                    const genericEtlOption = (etlFunctionsOptions || []).find((item: any) => item.value === 'generic')!;
                    const value = field.value ?? genericEtlOption;

                    return (
                      <div style={{ minWidth: '30%' }}>
                        <DropdownSelect
                          isDisabled={!enableEtl}
                          isLoading={isLoading}
                          value={value}
                          options={etlFunctionsOptions || []}
                          onChange={field.onChange}
                        />
                      </div>
                    );
                  }}
                />
              </StyledRowContainer>
            )}

            <FormButtons isLoading={isLoading} isDirty={isDirty} isValid={isValid} reset={reset} />
          </StyledContainer>
        </form>
      </NgCardContent>
    </NgCard>
  );
};
